import StyledTextFieldWithTitle from "@/modules/common/components/input/styled-text-field-with-title";
import { Box, Typography } from "@mui/material";
import { Controller, useWatch } from "react-hook-form";
import { PriceDetailsFormProps } from "./price-details-form";
import StyledTextField from "@/modules/common/components/input/styled-text-field";
import CustomAutoComplete from "@/modules/common/components/input/dropdowns/auto-complete";
import { PaymentTerms } from "@/modules/property-tracks/types";
import CustomLabel from "@/modules/common/components/input/custom-label";

export default function PriceDetailsInstallements({
  form: { control },
  namePrefix,
  extraFields = false,
}: PriceDetailsFormProps) {
  const fieldName = (field: string) =>
    namePrefix ? `${namePrefix}.${field}` : field;

  const [downpayment, remaining] = useWatch({
    control,
    name: [fieldName("downPayment"), fieldName("remainingInstallments")],
  });

  const totalPrice = (Number(downpayment) || 0) + (Number(remaining) || 0);

  return (
    <>
      <StyledTextFieldWithTitle
        value={totalPrice}
        title="Total Price"
        placeholder="Total Price"
        fullWidth
        formatNumeric
        size="small"
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: <Typography>EGP</Typography>,
          },
        }}
      />
      {extraFields && (
        <>
          <Box
            sx={(t) => ({
              display: "flex",
              flexDirection: "column",
              gap: t.nawy.spacing.xxs,
            })}
          >
            <CustomLabel
              sx={(t) => t.nawy.typography.english.variants.body.xs.regular}
            >
              Remaining duration
            </CustomLabel>
            <Box sx={(t) => ({ display: "flex", gap: t.nawy.spacing.xs })}>
              <Controller
                control={control}
                name={fieldName("durationRemainingYears")}
                render={({ field, fieldState }) => (
                  <StyledTextField
                    {...field}
                    formatNumeric
                    placeholder="years"
                    fullWidth
                    size="small"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    slotProps={{
                      input: {
                        endAdornment: <Typography>years</Typography>,
                      },
                    }}
                  />
                )}
              ></Controller>
              <Controller
                control={control}
                name={fieldName("durationRemainingMonths")}
                render={({ field, fieldState }) => (
                  <StyledTextField
                    {...field}
                    formatNumeric
                    placeholder="months"
                    fullWidth
                    size="small"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    slotProps={{
                      input: {
                        endAdornment: <Typography>months</Typography>,
                      },
                    }}
                  />
                )}
              ></Controller>
            </Box>
          </Box>
          {/* Installment frequency */}
          <Controller
            control={control}
            name={fieldName("installmentFrequency")}
            render={({ field, fieldState }) => (
              <CustomAutoComplete
                {...field}
                options={Object.entries(PaymentTerms).map(([key, value]) => ({
                  label: value,
                  value: key,
                }))}
                placeholder="Installment frequency"
                label="Installment frequency"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          ></Controller>
        </>
      )}
      <Controller
        control={control}
        name={fieldName("downPayment")}
        render={({ field, fieldState }) => (
          <StyledTextFieldWithTitle
            {...field}
            formatNumeric
            title="Downpayment (paid + over)"
            placeholder="Downpayment (paid + over)"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            slotProps={{
              input: {
                endAdornment: <Typography>EGP</Typography>,
              },
            }}
          />
        )}
      ></Controller>
      <Controller
        control={control}
        name={fieldName("remainingInstallments")}
        render={({ field, fieldState }) => (
          <StyledTextFieldWithTitle
            {...field}
            formatNumeric
            title="Remaining"
            placeholder="Remaining"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            slotProps={{
              input: {
                endAdornment: <Typography>EGP</Typography>,
              },
            }}
          />
        )}
      ></Controller>
      <Controller
        control={control}
        name={fieldName("maintenance")}
        render={({ field, fieldState }) => (
          <StyledTextFieldWithTitle
            {...field}
            formatNumeric
            title="Maintenance"
            placeholder="Maintenance"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            slotProps={{
              input: {
                endAdornment: <Typography>EGP</Typography>,
              },
            }}
          />
        )}
      ></Controller>
      <Controller
        control={control}
        name={fieldName("clubhouse")}
        render={({ field, fieldState }) => (
          <StyledTextFieldWithTitle
            {...field}
            formatNumeric
            title="Clubhouse"
            placeholder="Clubhouse"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            slotProps={{
              input: {
                endAdornment: <Typography>EGP</Typography>,
              },
            }}
          />
        )}
      ></Controller>
       <Controller
        control={control}
        name={fieldName("parking")}
        render={({ field, fieldState }) => (
          <StyledTextFieldWithTitle
            {...field}
            formatNumeric
            title="Parking"
            placeholder="Parking"
            fullWidth
            size="small"
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            slotProps={{
              input: {
                endAdornment: <Typography>EGP</Typography>,
              },
            }}
          />
        )}
      ></Controller>
    </>
  );
}
